import * as React from 'react';

import { cn } from '@/lib/utils';
import { Aide } from '@/components/ui/aide';

/**
 * Lecture d'un coup d'œil: les trois ou quatre chiffres qui disent où en est
 * l'argent, puis le détail rangé sous des questions simples.
 *
 * `ChiffresCles` reçoit des chiffres `{ cle, titre, valeur, unite?, etat?,
 * ton?, detail?, aide?, action? }`:
 *  - `etat`   un mot d'état ou de tendance (« Bénéfice », « 2 en retard »);
 *  - `ton`    'positif' | 'attention' | 'critique' | 'neutre': colore le mot
 *             d'état; le chiffre ne passe en rouge que s'il est négatif;
 *  - `detail` une ligne discrète sous le chiffre;
 *  - `aide`   l'explication, derrière une icône (voir Aide);
 *  - `action` `{ libelle, onClick }`: un lien d'action discret.
 *
 * Grille sans case orpheline: 4 chiffres en 2 × 2 puis 4 de front, 3 de
 * front dès la tablette, 2 côte à côte; une colonne sur téléphone.
 */
const GRILLES = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
};

const TONS_ETAT = {
  positif: 'bg-success/10 text-success',
  attention: 'bg-flame/10 text-flame-ink',
  critique: 'bg-destructive/10 text-destructive',
  neutre: 'bg-muted text-muted-foreground',
};

export const ChiffreCle = ({ titre, valeur, unite, etat, ton = 'neutre', detail, aide, action, negatif = false }) => (
  <article className="flex min-w-0 flex-col gap-1 rounded-xl border border-border bg-card p-3.5 shadow-sm" aria-label={titre}>
    <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1">
      <div className="flex min-w-0 items-center gap-1">
        <h3 className="min-w-0 text-sm font-medium text-muted-foreground">{titre}</h3>
        {aide && <Aide titre={titre}>{aide}</Aide>}
      </div>
      {etat && (
        <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', TONS_ETAT[ton] || TONS_ETAT.neutre)}>
          {etat}
        </span>
      )}
    </div>
    {/* Quatre de front à côté du menu (1280 px): « 113 240 000 FCFA » tient en text-xl. */}
    <p
      className={cn(
        'break-words text-xl font-semibold leading-tight tabular-nums sm:text-2xl xl:text-xl min-[1600px]:text-2xl',
        negatif ? 'text-destructive' : 'text-foreground',
      )}
    >
      {valeur}
      {unite && <span className="ml-1 text-sm font-medium text-muted-foreground">{unite}</span>}
    </p>
    {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="mt-auto self-start pt-1 text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {action.libelle}
      </button>
    )}
  </article>
);

export const ChiffresCles = ({ chiffres, className, label = 'L’essentiel' }) => {
  const liste = (chiffres || []).filter(Boolean).slice(0, 4);
  if (liste.length === 0) return null;
  return (
    <div
      role="group"
      aria-label={label}
      data-testid="chiffres-cles"
      className={cn('grid gap-3', GRILLES[liste.length], className)}
    >
      {liste.map(({ cle, ...chiffre }) => (
        <ChiffreCle key={cle || chiffre.titre} {...chiffre} />
      ))}
    </div>
  );
};

/**
 * Une partie de page posée comme une question (« Qu'est-ce que j'ai
 * gagné ? »): le titre, son aide éventuelle, une précision à droite (date),
 * puis le contenu.
 */
export const Question = ({ id, titre, aide, points, meta, children, className, niveau = 'h2' }) => {
  const Titre = niveau;
  return (
    <section aria-labelledby={id} className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <Titre id={id} className="min-w-0 text-base font-semibold text-foreground">{titre}</Titre>
          <Aide titre={titre} points={points}>{aide}</Aide>
        </div>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
      {children}
    </section>
  );
};

export default ChiffresCles;
